/**
 * 故事板（P-D⑤/⑥ 重做，蓝图 §4「向导式创作」）。
 *
 * 定位：面向小白的**模板驱动流水线**——第 1 步就选一个题材技能模板套用（复用 D-57 真模板），
 * 每步说清"将产出什么"，最后**一键到画布精修**（生成预置连线起手网）或**导出到剪辑**。
 * 与专业画布两条腿并存。数据为 mock 骨架，真实生成复用画布 generation 契约、接真后端落地。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Clapperboard, Download, Film, Layers, Sparkles, Users, Wand2 } from 'lucide-react';

import { SKILL_TEMPLATES, buildSkillCanvas } from '../../model/skills';
import { buildProjectFromShots } from '../../model/editing';
import { newCanvasId } from '../../features/canvas/canvasLibrary';
import { useCanvasStore } from '../../features/canvas/canvasStore';
import { useEditingStore } from '../editingStore';

const field = 'w-full rounded-control bg-surface-base px-2.5 py-2 text-ui text-ink-primary outline-none';

type Step = 1 | 2 | 3 | 4;

interface StepMeta {
  n: Step;
  label: string;
  hint: string;
  output: string;
}
const STEPS: StepMeta[] = [
  { n: 1, label: '选模板 · 起片', hint: '挑一个题材模板，填剧名与集数', output: '文学剧本 + 导演剧本（预置连线）' },
  { n: 2, label: '主体', hint: '从剧本自动抽取角色 / 场景 / 道具', output: '角色 / 场景 / 道具资产卡' },
  { n: 3, label: '分镜', hint: '逐集拆出分镜，填景别 / 画面 / 台词', output: '分镜节点（可生成关键帧 / 视频）' },
  { n: 4, label: '出片', hint: '一键到画布精修，或导出到剪辑', output: '成片工程' },
];

const MOCK_SUBJECTS = [
  { cat: '角色', name: '林晚', desc: '白裙、清冷、眼神有故事' },
  { cat: '角色', name: '陈默', desc: '黑色风衣、沉默寡言' },
  { cat: '场景', name: '出租屋', desc: '昏黄台灯、雨夜窗外' },
  { cat: '场景', name: '老城街巷', desc: '霓虹湿地、纵深透视' },
  { cat: '道具', name: '旧怀表', desc: '黄铜、刻痕、走停' },
];
const MOCK_SHOTS = [
  { i: 1, size: '远景', scene: '老城街巷', action: '雨夜，陈默撑伞独行', line: '旁白：那晚的雨，没洗掉任何东西。' },
  { i: 2, size: '中景', scene: '出租屋', action: '林晚合上抽屉', line: '林晚：你答应过我的。' },
  { i: 3, size: '特写', scene: '出租屋', action: '旧怀表停在 11:07', line: '陈默（自语）：时间会证明。' },
];

export function StoryboardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [skillId, setSkillId] = useState<string>(SKILL_TEMPLATES[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [episodeCount, setEpisodeCount] = useState(1);
  const [premise, setPremise] = useState('');

  const skill = SKILL_TEMPLATES.find((s) => s.id === skillId);
  const canGo = step > 1 || (title.trim() !== '' && premise.trim() !== '');

  function pickSkill(id: string): void {
    setSkillId(id);
    const s = SKILL_TEMPLATES.find((x) => x.id === id);
    if (s !== undefined) {
      setPremise(s.scriptPrompt);
      if (title === '') setTitle(`${s.name}·新作`);
    }
  }
  function next(): void {
    if (!canGo) return;
    setStep((v) => (Math.min(4, v + 1) as Step));
  }
  function prev(): void {
    setStep((v) => (Math.max(1, v - 1) as Step));
  }
  function toCanvas(): void {
    if (skill === undefined) return;
    useCanvasStore.getState().setDoc(buildSkillCanvas(skill, newCanvasId(), new Date().toISOString()));
    navigate('/canvas/editor');
  }
  function toEditing(): void {
    const id = `ep-${Date.now().toString(36)}`;
    useEditingStore.getState().addProject(buildProjectFromShots(id, `${title || '未命名'} · 成片`, MOCK_SHOTS.map((s) => ({ name: `镜${s.i}`, dur: 5 }))));
    navigate('/editing');
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* 左：步骤导航 */}
      <aside className="flex w-[280px] shrink-0 flex-col border-r border-surface-line bg-surface-raised/60 p-4">
        <div className="mb-1 flex items-center gap-2">
          <Clapperboard size={18} className="text-accent" />
          <h1 className="text-ui font-semibold">故事板</h1>
        </div>
        <p className="mb-4 text-uiSm text-ink-faint">选模板 → 逐步填充 → 一键出片。每步都会告诉你将产出什么。</p>
        <div className="flex flex-col gap-1">
          {STEPS.map((s) => {
            const state = s.n < step ? 'done' : s.n === step ? 'active' : 'todo';
            return (
              <button key={s.n} type="button" onClick={() => s.n < step && setStep(s.n)} className={`flex items-start gap-2.5 rounded-control px-2.5 py-2 text-left transition-colors ${state === 'active' ? 'bg-accent/15' : 'hover:bg-surface-overlay/60'} ${s.n < step ? 'cursor-pointer' : 'cursor-default'}`}>
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${state === 'todo' ? 'bg-surface-base text-ink-faint' : 'bg-accent text-accent-ink'}`}>
                  {state === 'done' ? <Check size={12} /> : s.n}
                </span>
                <span className="min-w-0">
                  <span className={`block truncate text-ui ${state === 'active' ? 'font-semibold text-accent' : 'text-ink-primary'}`}>{s.label}</span>
                  <span className="block truncate text-uiSm text-ink-faint">{s.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-surface-base">
          <div className="h-full rounded-full bg-accent/60 transition-all" style={{ width: `${(step / 4) * 100}%` }} />
        </div>
        <p className="mt-2 text-uiSm text-ink-faint">第 {step} / 4 步 · 将产出：<span className="text-accent">{STEPS[step - 1]?.output}</span></p>
      </aside>

      {/* 右：当前步 */}
      <main className="flex min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="mx-auto flex max-w-3xl flex-col gap-4">
              <h2 className="text-lg font-semibold">① 选一个题材模板起片</h2>
              <p className="text-uiSm text-ink-muted">模板已内置该题材的专业创作提示词与结构。选一个，我们帮你把整条流程铺好；也可稍后在画布自由改。</p>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {SKILL_TEMPLATES.map((s) => (
                  <button key={s.id} type="button" onClick={() => pickSkill(s.id)} className={`panel flex flex-col gap-1 p-3 text-left transition-colors ${skillId === s.id ? 'ring-2 ring-accent' : ''}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-ui font-semibold">{s.name}</span>
                      {skillId === s.id && <Check size={14} className="ml-auto text-accent" />}
                    </div>
                    <span className="text-[11px] text-ink-faint">{s.genre} · {s.blurb}</span>
                  </button>
                ))}
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-uiSm text-ink-faint">剧名</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="如：《长夜将尽》" className={field} />
              </label>
              <label className="flex w-40 flex-col gap-1">
                <span className="text-uiSm text-ink-faint">集数</span>
                <input type="number" min={1} max={100} value={episodeCount} onChange={(e) => setEpisodeCount(Math.max(1, Number(e.target.value) || 1))} className={field} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-uiSm text-ink-faint">故事梗概（已按模板预填，可改）</span>
                <textarea value={premise} onChange={(e) => setPremise(e.target.value)} placeholder="一段话讲清核心冲突与钩子…" className={`${field} h-32 resize-none leading-relaxed`} />
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="mx-auto flex max-w-3xl flex-col gap-4">
              <h2 className="text-lg font-semibold">② 主体（角色 / 场景 / 道具）</h2>
              <p className="text-uiSm text-ink-muted">从你的剧本自动抽取下列主体；<b className="text-ink-primary">到画布后可一键生成资产卡</b>（定妆图 / 三视图 / 一致性），此处为规划预览。</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {MOCK_SUBJECTS.map((s) => (
                  <div key={s.name} className="panel flex flex-col gap-1 p-3">
                    <span className="w-fit rounded bg-accent/15 px-1.5 text-[10px] text-accent">{s.cat}</span>
                    <span className="text-ui font-semibold">{s.name}</span>
                    <span className="text-uiSm text-ink-muted">{s.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mx-auto flex max-w-3xl flex-col gap-3">
              <h2 className="text-lg font-semibold">③ 分镜</h2>
              <p className="text-uiSm text-ink-muted">按剧本逐镜列出镜头要素；<b className="text-ink-primary">到画布后每镜可生成关键帧 / 配音 / 视频</b>。此处为分镜规划预览。</p>
              {MOCK_SHOTS.map((sh) => (
                <div key={sh.i} className="panel flex items-center gap-3 p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-surface-base font-num text-uiSm text-accent">{sh.i}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-uiSm text-ink-faint">
                      <span className="rounded bg-surface-base px-1.5">{sh.size}</span>
                      <span>{sh.scene}</span>
                    </div>
                    <p className="truncate text-ui text-ink-primary">{sh.action}</p>
                    <p className="truncate text-uiSm text-ink-muted">{sh.line}</p>
                  </div>
                  <Film size={16} className="shrink-0 text-ink-faint" />
                </div>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="mx-auto flex max-w-2xl flex-col gap-4">
              <h2 className="text-lg font-semibold">④ 出片</h2>
              <div className="panel flex flex-col gap-2 p-4 text-ui">
                <div className="flex justify-between"><span className="text-ink-muted">剧名</span><span className="font-semibold">{title || '—'}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">题材模板</span><span>{skill?.name ?? '—'}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">集数</span><span className="font-num">{episodeCount}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">主体</span><span className="font-num">{MOCK_SUBJECTS.length}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">分镜</span><span className="font-num">{MOCK_SHOTS.length}</span></div>
              </div>
              <div className="flex flex-col gap-2">
                <button type="button" onClick={toCanvas} className="btn-primary inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-ui">
                  <Wand2 size={16} />
                  到画布精修（生成预置连线起手网）
                </button>
                <button type="button" onClick={toEditing} className="btn-ghost inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-ui">
                  <Download size={16} />
                  导出到剪辑
                </button>
              </div>
              <p className="text-uiSm text-ink-faint">推荐：先「到画布精修」把主体/分镜/关键帧生成出来，再「导出到剪辑」粗排成片。</p>
            </div>
          )}
        </div>

        {/* 底部导航 */}
        <div className="flex items-center justify-between border-t border-surface-line px-6 py-3">
          <button type="button" onClick={prev} disabled={step === 1} className="btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-ui disabled:opacity-40">
            上一步
          </button>
          {step < 4 ? (
            <button type="button" onClick={next} disabled={!canGo} className="btn-primary inline-flex items-center gap-1 px-4 py-1.5 text-ui disabled:opacity-40" title={canGo ? '' : '请先选模板并填写剧名与梗概'}>
              {step === 1 && <Sparkles size={14} />}
              {step === 2 && <Users size={14} />}
              {step === 3 && <Layers size={14} />}
              下一步
              <ArrowRight size={14} />
            </button>
          ) : (
            <span className="text-uiSm text-ink-faint">已是最后一步</span>
          )}
        </div>
      </main>
    </div>
  );
}
